import { ApolloProvider } from "@apollo/client";
import { WarningFilled } from "@carbon/icons-react";
import { FileUploader, Stack } from "@carbon/react";
import { FC } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { ConfirmActionModal } from "src/components/Modal";
import ImportSpecificationSinglePickerFormField from "src/components/Pickers/ImportSpecificationSinglePickerFormField.tsx";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError, useMapToMutationError } from "src/errors";
import { createApolloClient } from "src/graphql/import-export/apolloClient.ts";
import { useCreateImportMutation } from "src/graphql/import-export/queries/ImportMutation.ts";
import { emitError } from "src/hooks/useEvents.ts";
import { useImportFile } from "src/hooks/useImportFile.ts";
import { useT } from "src/internationalization";
import { CaseType } from "src/types/case";
import { ImportSpecification, ImportSpecificationType } from "src/types/importSpecification";

export type ImportProps = {
  onClose: () => void;
  importSpecType: ImportSpecificationType;
  caseType?: CaseType;
};

type ImportPayload = {
  file: File;
  importSpecification: ImportSpecification;
};

const apolloClient = createApolloClient(emitError);

const ImportModal: FC<ImportProps> = ({ onClose, importSpecType, caseType }) => {
  const { showSuccessToast } = useToast();
  const t = useT();
  const navigate = useNavigate();
  const mapToMutationError = useMapToMutationError();

  const { performMutation, loading, error } = useCreateImportMutation({
    onSuccess: (data) => {
      showSuccessToast(t("import.modal.toast.success.message"));
      if (data?.import) {
        navigate(`/import-export/job/${data?.import}`);
      }
      onClose();
    },
  });

  const methods = useForm<ImportPayload>();
  const { handleSubmit } = methods;
  const { importFile, loading: importLoading, error: importError } = useImportFile();

  const onSubmit: SubmitHandler<ImportPayload> = async (data) => {
    const response = await importFile(data.file);
    performMutation({
      importSpecificationId: data.importSpecification.id,
      fileId: response.id,
    });
  };

  return (
    <ConfirmActionModal
      title={t("import.modal.title", {
        type:
          importSpecType === "case"
            ? t("import.modal.title.type.cases")
            : t("import.modal.title.type.blacklist"),
      })}
      subtitle={importSpecType === "case" && caseType ? caseType.name : undefined}
      closeOnOutsideClick={false}
      closeOnEscape={false}
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonText={t("import.button.import")}
      error={mapToModalError(mapToMutationError(importError) ?? error)}
      loading={importLoading || loading}
      initialFocus="#importSpecificationPicker"
    >
      <ApolloProvider client={apolloClient}>
        <FormProvider {...methods}>
          <Stack gap={5}>
            <ImportSpecificationSinglePickerFormField
              id="importSpecificationPicker"
              name={"importSpecification"}
              rules={{ required: true }}
              titleText={t("import.modal.form.specification.title")}
              helperText=""
              invalidText={t("import.modal.form.specification.invalid")}
              type={importSpecType}
              caseType={caseType}
            />

            <Controller
              rules={{
                required: t("import.modal.form.file.invalid"),
              }}
              render={({ field, formState }) => {
                return (
                  <>
                    <FileUploader
                      iconDescription={t("import.modal.form.file.icon.description")}
                      labelTitle={t("import.modal.form.file.title")}
                      labelDescription={t("import.modal.form.file.description")}
                      buttonLabel={t("import.modal.form.file.button")}
                      name="file"
                      filenameStatus="edit"
                      accept={[".csv"]}
                      multiple={false}
                      onDelete={() => field.onChange(undefined)}
                      onChange={(event) => field.onChange(event.target.files[0])}
                    />
                    {formState.errors.file && (
                      <span className="text-textError flex gap-2 items-center">
                        <WarningFilled /> {formState.errors.file.message?.toString()}
                      </span>
                    )}
                  </>
                );
              }}
              name={"file"}
            />
          </Stack>
        </FormProvider>
      </ApolloProvider>
    </ConfirmActionModal>
  );
};

export default ImportModal;
